import React, { useEffect, useState, useCallback } from 'react';
import Layout from '../components/Layout';
import api from '../api/axios';
import { C } from '../theme';

// ── Searchable dropdown ───────────────────────────────────────────────────────
function SearchDropdown({ options, value, onChange, placeholder, getLabel, getMeta }) {
  const [search, setSearch] = useState('');
  const [open,   setOpen]   = useState(false);

  const selected = value != null ? options.find(o => o.id === value) : null;

  const filtered = options.filter(o => {
    if (!search.trim()) return true;
    const q = search.toLowerCase();
    return getLabel(o).toLowerCase().includes(q) ||
      (getMeta ? getMeta(o).toLowerCase().includes(q) : false);
  });

  if (selected && !open) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 12px', border: `1.5px solid ${C.primary}`, borderRadius: '8px', background: C.primaryLight }}>
        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontSize: '13px', fontWeight: 700, color: C.primaryDeep, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
            {getLabel(selected)}
          </div>
          {getMeta && (
            <div style={{ fontSize: '11px', color: C.textSub, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
              {getMeta(selected)}
            </div>
          )}
        </div>
        <button type="button" onClick={() => { setSearch(''); setOpen(true); }}
          style={{ fontSize: '11px', padding: '3px 9px', borderRadius: '5px', background: `${C.primary}20`, color: C.primaryDeep, border: `1px solid ${C.primary}40`, cursor: 'pointer', fontWeight: 600 }}>
          Change
        </button>
        <button type="button" onClick={() => onChange(null)}
          style={{ background: 'none', border: 'none', cursor: 'pointer', color: C.textSub, fontSize: '16px', lineHeight: 1, padding: '2px 4px' }}>
          ✕
        </button>
      </div>
    );
  }

  return (
    <div>
      <input
        autoFocus={open}
        value={search}
        onChange={e => { setSearch(e.target.value); setOpen(true); }}
        onFocus={() => setOpen(true)}
        onBlur={() => setTimeout(() => setOpen(false), 180)}
        placeholder={placeholder || 'Search…'}
        style={{ width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box' }}
      />
      {open && (
        <div style={{ marginTop: '4px', border: `1.5px solid ${C.border}`, borderRadius: '8px', maxHeight: '220px', overflowY: 'auto', background: C.white, boxShadow: '0 4px 16px rgba(0,0,0,0.08)' }}>
          {filtered.length === 0 ? (
            <div style={{ padding: '14px', textAlign: 'center', color: C.textSub, fontSize: '12px' }}>
              {options.length === 0 ? 'No options available' : 'No results found'}
            </div>
          ) : filtered.slice(0, 60).map(o => (
            <div key={o.id}
              onMouseDown={() => { onChange(o.id); setSearch(''); setOpen(false); }}
              style={{ padding: '10px 14px', cursor: 'pointer', borderBottom: `1px solid ${C.border}`, background: value === o.id ? C.primaryLight : 'transparent' }}
              onMouseEnter={e => { if (value !== o.id) e.currentTarget.style.background = '#F9FAFB'; }}
              onMouseLeave={e => { if (value !== o.id) e.currentTarget.style.background = 'transparent'; }}
            >
              <div style={{ fontSize: '13px', fontWeight: 600, color: C.textMain }}>{getLabel(o)}</div>
              {getMeta && <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>{getMeta(o)}</div>}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────────────────────────────────────

const STATUS_CFG = {
  open:          { label: 'Open',        bg: '#DBEAFE', color: '#1E40AF', dot: '#3B82F6' },
  'in-progress': { label: 'In Progress', bg: '#FEF3C7', color: '#92400E', dot: '#F59E0B' },
  resolved:      { label: 'Resolved',    bg: '#D1FAE5', color: '#065F46', dot: '#10B981' },
  closed:        { label: 'Closed',      bg: '#F3F4F6', color: '#374151', dot: '#9CA3AF' },
};
const STATUSES   = ['open', 'in-progress', 'resolved', 'closed'];
const emptyForm  = { client_id: null, installer_id: null, description: '', status: 'open', notes: '' };

export default function Complaint() {
  const [rows,       setRows]       = useState([]);
  const [clients,    setClients]    = useState([]);
  const [installers, setInstallers] = useState([]);
  const [loading,    setLoading]    = useState(true);
  const [search,     setSearch]     = useState('');
  const [stFilter,   setStFilter]   = useState('');
  const [modal,      setModal]      = useState(null);  // null | 'add' | row-object
  const [form,       setForm]       = useState(emptyForm);
  const [saving,     setSaving]     = useState(false);
  const [err,        setErr]        = useState('');
  const [toast,      setToast]      = useState(null);
  const [delRow,     setDelRow]     = useState(null);
  const [deleting,   setDeleting]   = useState(false);

  // Raise service
  const [svcRow,      setSvcRow]      = useState(null);   // complaint to raise service for
  const [svcInstaller, setSvcInstaller] = useState('');
  const [svcReason,   setSvcReason]   = useState('');
  const [svcBusy,     setSvcBusy]     = useState(false);
  const [svcErr,      setSvcErr]      = useState('');

  const showToast = (type, text) => {
    setToast({ type, text });
    setTimeout(() => setToast(null), 4000);
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [cmpR, cliR, usrR] = await Promise.all([
        api.get('/api/complaints'),
        api.get('/api/clients'),
        api.get('/api/users'),
      ]);
      setRows(cmpR.data);
      setClients(cliR.data.filter(c => !c.dealer_id));
      setInstallers(usrR.data.filter(u => u.role === 'installer'));
    } catch {}
    finally { setLoading(false); }
  }, []);

  useEffect(() => { load(); }, [load]);

  const openAdd = () => { setForm(emptyForm); setErr(''); setModal('add'); };

  const openRaiseService = (row) => { setSvcRow(row); setSvcInstaller(''); setSvcReason(''); setSvcErr(''); };
  const confirmRaiseService = async () => {
    if (!svcInstaller) { setSvcErr('Select an installer to assign.'); return; }
    setSvcBusy(true); setSvcErr('');
    try {
      await api.post(`/api/complaints/${svcRow.id}/raise-service`, {
        installer_id: Number(svcInstaller),
        reason: svcReason.trim() || null,
      });
      setSvcRow(null);
      load();
      showToast('success', 'Service raised — see the Service module.');
    } catch (e) {
      setSvcErr(e?.response?.data?.detail || 'Could not raise service.');
    } finally { setSvcBusy(false); }
  };
  const openEdit = (row) => {
    setForm({
      client_id:    row.client_id,
      installer_id: row.installer_id,
      description:  row.description,
      status:       row.status,
      notes:        row.notes || '',
    });
    setErr('');
    setModal(row);
  };

  const save = async () => {
    if (!form.client_id)           return setErr('Select a client.');
    if (!form.description?.trim()) return setErr('Description is required.');
    setSaving(true); setErr('');
    try {
      const payload = {
        client_id:    form.client_id,
        installer_id: form.installer_id || null,   // assigned later via Raise Service
        description:  form.description.trim(),
        status:       form.status,
        notes:        form.notes?.trim() || null,
      };
      if (modal === 'add') {
        await api.post('/api/complaints', payload);
        showToast('success', 'Complaint created.');
      } else {
        await api.put(`/api/complaints/${modal.id}`, payload);
        showToast('success', 'Complaint updated.');
      }
      setModal(null);
      load();
    } catch (e) {
      setErr(e?.response?.data?.detail || 'Save failed.');
    } finally { setSaving(false); }
  };

  const confirmDelete = async () => {
    if (!delRow) return;
    setDeleting(true);
    try {
      await api.delete(`/api/complaints/${delRow.id}`);
      showToast('success', `${delRow.complaint_id} deleted.`);
      setDelRow(null);
      load();
    } catch (e) {
      showToast('error', e?.response?.data?.detail || 'Delete failed.');
    } finally { setDeleting(false); }
  };

  const filtered = rows.filter(r => {
    const q = search.toLowerCase();
    return (
      (!stFilter || r.status === stFilter) &&
      (!q || r.complaint_id?.toLowerCase().includes(q)
          || r.client_name?.toLowerCase().includes(q)
          || r.installer_name?.toLowerCase().includes(q)
          || r.description?.toLowerCase().includes(q))
    );
  });

  const counts = {
    total:      rows.length,
    open:       rows.filter(r => r.status === 'open').length,
    inProgress: rows.filter(r => r.status === 'in-progress').length,
    resolved:   rows.filter(r => r.status === 'resolved').length,
  };

  return (
    <Layout title="Complaints">

      {/* Toast */}
      {toast && (
        <div style={{ position: 'fixed', top: 24, right: 24, zIndex: 9999,
          background: toast.type === 'success' ? '#D1FAE5' : '#FEE2E2',
          color: toast.type === 'success' ? '#065F46' : '#991B1B',
          padding: '12px 20px', borderRadius: '10px', fontWeight: 600, fontSize: '14px',
          boxShadow: '0 4px 16px rgba(0,0,0,0.12)' }}>
          {toast.text}
        </div>
      )}

      {/* Stats */}
      <div style={{ display: 'flex', gap: '14px', marginBottom: '24px', flexWrap: 'wrap', alignItems: 'flex-start' }}>
        {[
          { label: 'Total',       val: counts.total,      color: C.primary,  bg: C.primaryLight, border: `${C.primary}60` },
          { label: 'Open',        val: counts.open,       color: '#1E40AF',  bg: '#DBEAFE',      border: '#BFDBFE' },
          { label: 'In Progress', val: counts.inProgress, color: '#92400E',  bg: '#FEF3C7',      border: '#FDE68A' },
          { label: 'Resolved',    val: counts.resolved,   color: '#065F46',  bg: '#D1FAE5',      border: '#6EE7B7' },
        ].map(s => (
          <div key={s.label} style={{ background: s.bg, border: `1.5px solid ${s.border}`, borderRadius: '12px', padding: '14px 22px', minWidth: '110px' }}>
            <div style={{ fontSize: '24px', fontWeight: 800, color: s.color }}>{s.val}</div>
            <div style={{ fontSize: '11px', color: C.textSub, fontWeight: 600, marginTop: '2px' }}>{s.label}</div>
          </div>
        ))}
        <div style={{ flex: 1 }} />
        <button onClick={openAdd} style={btnPrimary}>+ Add Complaint</button>
      </div>

      {/* Table card */}
      <div style={{ background: C.white, borderRadius: '12px', padding: '20px', border: `1px solid ${C.border}` }}>

        {/* Filters */}
        <div style={{ display: 'flex', gap: '10px', marginBottom: '16px', flexWrap: 'wrap', alignItems: 'center' }}>
          <input
            placeholder="Search ID, client, installer, description…"
            value={search} onChange={e => setSearch(e.target.value)}
            style={{ flex: 1, minWidth: '200px', padding: '9px 14px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none' }}
          />
          <select value={stFilter} onChange={e => setStFilter(e.target.value)} style={selSt}>
            <option value="">All Status</option>
            {STATUSES.map(s => <option key={s} value={s}>{STATUS_CFG[s]?.label}</option>)}
          </select>
          {(search || stFilter) && (
            <button onClick={() => { setSearch(''); setStFilter(''); }} style={btnGhost}>Clear</button>
          )}
          <span style={{ fontSize: '12px', color: C.textSub }}>{filtered.length} / {rows.length}</span>
        </div>

        {/* Table */}
        <div style={{ overflowX: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', minWidth: '700px' }}>
            <thead>
              <tr style={{ background: C.primaryBg, borderBottom: `2px solid ${C.border}` }}>
                {['Complaint ID', 'Client', 'Installer', 'Description', 'Status', 'Date', 'Actions'].map(h => (
                  <th key={h} style={th}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr><td colSpan={7} style={emptyCell}>Loading…</td></tr>
              ) : filtered.length === 0 ? (
                <tr><td colSpan={7} style={emptyCell}>
                  {rows.length === 0 ? 'No complaints yet. Click "+ Add Complaint" to create one.' : 'No results match the filters.'}
                </td></tr>
              ) : filtered.map((r, i) => {
                const st = STATUS_CFG[r.status] || STATUS_CFG.open;
                return (
                  <tr key={r.id} style={{ background: i % 2 === 0 ? C.white : '#FDFAFF', borderBottom: `1px solid ${C.border}` }}>
                    <td style={td}>
                      <span style={{ fontFamily: 'monospace', fontSize: '12px', fontWeight: 700, color: C.primaryDeep }}>{r.complaint_id}</span>
                    </td>
                    <td style={td}>
                      <div style={{ fontWeight: 600, fontSize: '13px', color: C.textMain }}>{r.client_name}</div>
                      <div style={{ fontSize: '11px', color: C.textSub, fontFamily: 'monospace' }}>{r.client_ref}</div>
                    </td>
                    <td style={td}>
                      {r.installer_name
                        ? <span style={{ fontSize: '13px', color: C.textMain }}>{r.installer_name}</span>
                        : <span style={{ fontSize: '12px', color: C.textSub, fontStyle: 'italic' }}>Unassigned</span>}
                      {r.service_ref && (
                        <div style={{ fontSize: '10px', color: '#0369A1', fontWeight: 700, marginTop: '3px' }}>🛠️ {r.service_ref} ({r.service_status})</div>
                      )}
                    </td>
                    <td style={{ ...td, maxWidth: '260px' }}>
                      <span style={{ fontSize: '13px', color: C.textMain, display: '-webkit-box', WebkitLineClamp: 2, WebkitBoxOrient: 'vertical', overflow: 'hidden' }}>
                        {r.description}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ padding: '4px 12px', borderRadius: '20px', fontSize: '11px', fontWeight: 600, background: st.bg, color: st.color, whiteSpace: 'nowrap' }}>
                        <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: st.dot, display: 'inline-block', marginRight: '5px', verticalAlign: 'middle' }} />
                        {st.label}
                      </span>
                    </td>
                    <td style={td}>
                      <span style={{ fontSize: '12px', color: C.textSub }}>{r.created_at}</span>
                      {r.resolved_at && <div style={{ fontSize: '11px', color: '#059669' }}>Resolved: {r.resolved_at}</div>}
                    </td>
                    <td style={td}>
                      <div style={{ display: 'flex', gap: '6px', flexWrap: 'wrap' }}>
                        {!r.service_id && r.status !== 'resolved' && r.status !== 'closed' && (
                          <button onClick={() => openRaiseService(r)} style={{ padding: '5px 11px', borderRadius: '7px', fontSize: '12px', fontWeight: 700, background: '#E0F2FE', color: '#0369A1', border: '1px solid #BAE6FD', cursor: 'pointer' }}>🛠️ Raise Service</button>
                        )}
                        <button onClick={() => openEdit(r)} style={btnEdit}>Edit</button>
                        <button onClick={() => setDelRow(r)} style={btnDel}>Delete</button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>

      {/* Add / Edit Modal */}
      {modal !== null && (
        <div style={overlay}>
          <div style={modalBox}>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' }}>
              <h3 style={{ margin: 0, fontSize: '17px', fontWeight: 700, color: C.textMain }}>
                {modal === 'add' ? 'New Complaint' : `Edit ${modal.complaint_id}`}
              </h3>
              <button onClick={() => setModal(null)} style={btnClose}>✕</button>
            </div>

            <label style={labelSt}>Client *</label>
            <SearchDropdown
              options={clients}
              value={form.client_id}
              onChange={id => setForm(f => ({ ...f, client_id: id }))}
              placeholder="Search client name or ID…"
              getLabel={c => c.client_name}
              getMeta={c => c.client_id}
            />

            {modal !== 'add' && (
              <>
                <label style={labelSt}>Installer</label>
                <select
                  value={form.installer_id ?? ''}
                  onChange={e => setForm(f => ({ ...f, installer_id: e.target.value ? Number(e.target.value) : null }))}
                  style={inputSt}
                >
                  <option value="">— Unassigned —</option>
                  {installers.map(u => (
                    <option key={u.id} value={u.id}>{u.full_name || u.username}</option>
                  ))}
                </select>
              </>
            )}

            <label style={labelSt}>Description / Complaint *</label>
            <textarea
              value={form.description}
              onChange={e => setForm(f => ({ ...f, description: e.target.value }))}
              rows={3}
              placeholder="Describe the complaint…"
              style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }}
            />

            <label style={labelSt}>Status</label>
            <select value={form.status} onChange={e => setForm(f => ({ ...f, status: e.target.value }))} style={inputSt}>
              {STATUSES.map(s => <option key={s} value={s}>{STATUS_CFG[s]?.label}</option>)}
            </select>

            <label style={labelSt}>Notes (optional)</label>
            <textarea
              value={form.notes}
              onChange={e => setForm(f => ({ ...f, notes: e.target.value }))}
              rows={2}
              placeholder="Any additional notes…"
              style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }}
            />

            {err && <div style={{ color: '#DC2626', fontSize: '13px', margin: '10px 0 4px' }}>{err}</div>}

            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end', marginTop: '16px' }}>
              <button onClick={() => setModal(null)} style={btnGhost}>Cancel</button>
              <button onClick={save} disabled={saving} style={btnPrimary}>{saving ? 'Saving…' : 'Save'}</button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Confirm */}
      {delRow && (
        <div style={overlay}>
          <div style={{ ...modalBox, maxWidth: '400px' }}>
            <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: 700, color: C.textMain }}>Delete Complaint</h3>
            <p style={{ color: C.textSub, fontSize: '14px', marginBottom: '20px' }}>
              Delete <strong>{delRow.complaint_id}</strong> for <strong>{delRow.client_name}</strong>? This cannot be undone.
            </p>
            <div style={{ display: 'flex', gap: '10px', justifyContent: 'flex-end' }}>
              <button onClick={() => setDelRow(null)} style={btnGhost}>Cancel</button>
              <button onClick={confirmDelete} disabled={deleting}
                style={{ ...btnPrimary, background: '#EF4444', borderColor: '#EF4444' }}>
                {deleting ? 'Deleting…' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* ── Raise Service modal ── */}
      {svcRow && (
        <div style={{ position: 'fixed', inset: 0, background: 'rgba(28,20,7,0.6)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1000, padding: '16px' }}>
          <div style={{ background: C.white, borderRadius: '16px', width: '100%', maxWidth: '420px' }}>
            <div style={{ padding: '17px 22px', borderBottom: `1px solid ${C.border}` }}>
              <div style={{ fontSize: '15px', fontWeight: 800, color: C.textMain }}>🛠️ Raise Service</div>
              <div style={{ fontSize: '11px', color: C.textSub, marginTop: '2px' }}>{svcRow.complaint_id} · {svcRow.client_name}</div>
            </div>
            <div style={{ padding: '18px 22px' }}>
              <label style={labelSt}>Assign installer *</label>
              <select value={svcInstaller} onChange={e => { setSvcInstaller(e.target.value); setSvcErr(''); }} style={inputSt}>
                <option value="">— Select installer —</option>
                {installers.map(u => <option key={u.id} value={u.id}>{u.full_name || u.username}</option>)}
              </select>
              <label style={{ ...labelSt, marginTop: '12px' }}>Service reason</label>
              <textarea value={svcReason} onChange={e => setSvcReason(e.target.value)} rows={2}
                placeholder={`Defaults to the complaint: "${(svcRow.description || '').slice(0, 60)}…"`}
                style={{ ...inputSt, resize: 'vertical', fontFamily: 'inherit' }} />
              <div style={{ marginTop: '12px', padding: '10px 13px', background: '#F0F9FF', border: '1px solid #BAE6FD', borderRadius: '9px', fontSize: '11px', color: '#0369A1', lineHeight: 1.5 }}>
                ℹ This creates a Service record (in the Service module) for the assigned installer. The complaint is marked <strong>service-raised</strong>.
              </div>
              {svcErr && <div style={{ marginTop: '10px', color: C.danger, fontSize: '12px', fontWeight: 600 }}>{svcErr}</div>}
            </div>
            <div style={{ padding: '13px 22px', borderTop: `1px solid ${C.border}`, display: 'flex', gap: '9px', justifyContent: 'flex-end' }}>
              <button onClick={() => setSvcRow(null)} disabled={svcBusy} style={btnEdit}>Cancel</button>
              <button onClick={confirmRaiseService} disabled={svcBusy} style={{ padding: '9px 20px', background: svcBusy ? '#9CA3AF' : 'linear-gradient(135deg,#0EA5E9,#0369A1)', color: '#fff', border: 'none', borderRadius: '8px', fontSize: '13px', fontWeight: 800, cursor: svcBusy ? 'not-allowed' : 'pointer' }}>
                {svcBusy ? 'Raising…' : '🛠️ Raise Service'}
              </button>
            </div>
          </div>
        </div>
      )}
    </Layout>
  );
}

const th        = { textAlign: 'left', padding: '11px 14px', fontSize: '11px', fontWeight: 700, color: C.textSub, textTransform: 'uppercase', letterSpacing: '0.5px' };
const td        = { padding: '12px 14px', fontSize: '13px', color: C.textMain, verticalAlign: 'middle' };
const emptyCell = { textAlign: 'center', padding: '52px', color: C.textSub, fontSize: '13px' };
const selSt     = { padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', background: C.white, cursor: 'pointer' };
const btnPrimary= { padding: '9px 20px', background: C.primary, color: C.white, border: `1px solid ${C.primaryDark}`, borderRadius: '8px', fontSize: '13px', fontWeight: 700, cursor: 'pointer' };
const btnGhost  = { padding: '9px 16px', background: C.primaryBg, color: C.textSub, border: `1px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', fontWeight: 600, cursor: 'pointer' };
const btnEdit   = { padding: '6px 14px', background: C.primaryBg, color: C.primaryDeep, border: `1px solid ${C.border}`, borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' };
const btnDel    = { padding: '6px 14px', background: '#FEE2E2', color: '#991B1B', border: '1px solid #FECACA', borderRadius: '6px', fontSize: '12px', fontWeight: 600, cursor: 'pointer' };
const btnClose  = { background: 'none', border: 'none', fontSize: '18px', cursor: 'pointer', color: C.textSub, lineHeight: 1 };
const overlay   = { position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.45)', zIndex: 1000, display: 'flex', alignItems: 'center', justifyContent: 'center', padding: '20px' };
const modalBox  = { background: C.white, borderRadius: '14px', padding: '28px', width: '100%', maxWidth: '520px', boxShadow: '0 20px 60px rgba(0,0,0,0.2)', maxHeight: '90vh', overflowY: 'auto' };
const labelSt   = { display: 'block', fontSize: '12px', fontWeight: 700, color: C.textSub, marginBottom: '5px', marginTop: '14px', textTransform: 'uppercase', letterSpacing: '0.4px' };
const inputSt   = { width: '100%', padding: '9px 12px', border: `1.5px solid ${C.border}`, borderRadius: '8px', fontSize: '13px', outline: 'none', boxSizing: 'border-box', background: C.white };
